import { createContext, useContext, useState } from 'react'

export class DirectDebitStatus {
  static EMAIL_VERIFICATION = 'email-verification'
  static OTP_VERIFICATION = 'otp-verification'
  static ACCOUNT_DETAILS = 'account-details'
  static TRANSFER_CONFIRMATION = 'transfer-confirmation'
  static SUCCESS = 'success'
  static FAILED = 'failed'
  static EXPIRED = 'expired'

  constructor(type) {
    this.type = type
  }
}

export const DirectDebitStatusContext = createContext(null)

export const DirectDebitStatusContextProvider = ({ children }) => {
  const [directDebitStatus, setDirectDebitStatus] = useState({
    status: DirectDebitStatus.EMAIL_VERIFICATION,
    message: '',
  })

  const [userEmail, setUserEmail] = useState('')
  const [userPhone, setUserPhone] = useState('')
  const [userAddress, setUserAddress] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [selectedBank, setSelectedBank] = useState({
    bankPublicId: '',
    bankName: '',
  })
  const [accountName, setAccountName] = useState('')
  const [transferDetails, setTransferDetails] = useState(null)
  const [otpRequestedAt, setOtpRequestedAt] = useState(null)

  // Session-related state
  const [sessionReference, _setSessionReference] = useState(null)
  const [sessionKey, _setSessionKey] = useState(null)
  const [sessionId, _setSessionId] = useState(null)
  const [otpSessionId, _setOtpSessionId] = useState(null)
  const [otpChannel, _setOtpChannel] = useState('email')
  const [otpDestination, _setOtpDestination] = useState(null)
  const [sessionStatus, _setSessionStatus] = useState(null)
  const [checkoutUrl, _setCheckoutUrl] = useState(null)

  const showEmailVerification = (message = '') => {
    setDirectDebitStatus({
      status: DirectDebitStatus.EMAIL_VERIFICATION,
      message: message,
    })
  }

  const showOTPVerification = (message = '') => {
    setOtpRequestedAt(new Date())
    setDirectDebitStatus({
      status: DirectDebitStatus.OTP_VERIFICATION,
      message: message,
    })
  }

  const showAccountDetails = (message = '') => {
    setDirectDebitStatus({
      status: DirectDebitStatus.ACCOUNT_DETAILS,
      message: message,
    })
  }

  const showTransferConfirmation = (transferData, message = '') => {
    setTransferDetails(transferData)
    setDirectDebitStatus({
      status: DirectDebitStatus.TRANSFER_CONFIRMATION,
      message: message,
    })
  }

  const showSuccess = () => {
    setDirectDebitStatus({
      status: DirectDebitStatus.SUCCESS,
      message: '',
    })
  }

  const showFailed = (message = '') => {
    setDirectDebitStatus({
      status: DirectDebitStatus.FAILED,
      message: message,
    })
  }

  const showExpired = (message = '') => {
    setDirectDebitStatus({
      status: DirectDebitStatus.EXPIRED,
      message: message,
    })
  }

  const resetFlow = () => {
    setDirectDebitStatus({
      status: DirectDebitStatus.EMAIL_VERIFICATION,
      message: '',
    })
    setUserEmail('')
    setUserPhone('')
    setUserAddress('')
    setAccountNumber('')
    setSelectedBank({ code: '', bankName: '' })
    setAccountName('')
    setTransferDetails(null)
    setOtpRequestedAt(null)
  }

  const setErrorMessage = (message) => {
    setDirectDebitStatus((prevStatus) => ({
      ...prevStatus,
      message: message,
    }))
  }

  // Session-related setters
  const setSessionReference = (reference) => {
    _setSessionReference(reference)
  }

  const setSessionKey = (key) => {
    _setSessionKey(key)
  }

  const setSessionId = (id) => {
    _setSessionId(id)
  }

  const setOtpSessionId = (id) => {
    _setOtpSessionId(id)
  }

  const setOtpChannel = (channel) => {
    _setOtpChannel(channel)
  }

  const setOtpDestination = (destination) => {
    _setOtpDestination(destination)
  }

  const setSessionStatus = (status) => {
    _setSessionStatus(status)
  }

  const setCheckoutUrl = (url) => {
    _setCheckoutUrl(url)
  }

  const value = {
    directDebitStatus,
    userEmail,
    setUserEmail,
    userPhone,
    setUserPhone,
    userAddress,
    setUserAddress,
    accountNumber,
    setAccountNumber,
    selectedBank,
    setSelectedBank,
    accountName,
    setAccountName,
    transferDetails,
    setTransferDetails,
    otpRequestedAt,
    setOtpRequestedAt,
    sessionReference,
    setSessionReference,
    sessionKey,
    setSessionKey,
    sessionId,
    setSessionId,
    otpSessionId,
    setOtpSessionId,
    otpChannel,
    setOtpChannel,
    otpDestination,
    setOtpDestination,
    sessionStatus,
    setSessionStatus,
    checkoutUrl,
    setCheckoutUrl,
    showEmailVerification,
    showOTPVerification,
    showAccountDetails,
    showTransferConfirmation,
    showSuccess,
    showFailed,
    showExpired,
    resetFlow,
    setErrorMessage,
  }

  return (
    <DirectDebitStatusContext.Provider value={value}>
      {children}
    </DirectDebitStatusContext.Provider>
  )
}

export const useDirectDebitStatus = () => {
  return useContext(DirectDebitStatusContext)
}
